import { useEffect, useState } from 'react';
import { Row, Col } from 'reactstrap';
import UserModal, { UserModalType } from '../user/UserModal';
import ReactLoading from 'react-loading';
import { makeStyles } from '@material-ui/core';
import { useGetGroupsQuery, useGetOfficesQuery, useGetCurrentUserQuery, useUserSearchQuery } from 'utils/wemble-api';
import { UserDto, GroupDto } from 'utils/wemble-api.generated';
import classNames from 'classnames';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { Link } from 'react-router-dom';
import PageTitle from 'components/PageTitle';
import { SIDEBAR_WIDTH } from 'components/navigation/Sidebar';

const useStyles = makeStyles(() => ({
  topbar: {
    height: '75px',
    width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
    marginLeft: SIDEBAR_WIDTH,
    position: 'fixed',
    zIndex: 1,
    top: 0,
    left: 0,
    backgroundColor: 'white',
    overflowX: 'hidden',
    paddingLeft: '8px',
    paddingRight: '10px',
    paddingTop: '8px',
    borderBottom: '1px solid #FAFBFD',
    textAlign: 'left',
    borderTop: '1px solid #EAEBF1',
    '@media (max-width: 467px)': {
      display: 'none',
    },
  },
  searchInput: {
    marginLeft: 14,
    border: 'none',
    color: '#8E93A2',
    width: 'calc(100% - 44px)',
    height: '60px',
  },
  title: {
    color: '#2B2E39',
    display: 'inline',
    fontWeight: 'bolder',
  },
  loader: {
    fill: '#59abcb',
    margin: 'auto',
    marginTop: 50,
    width: '25%',
  },
  content: {
    marginTop: 100,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: 20,
    backgroundColor: 'white',
    borderRadius: '0.25rem',
    overflow: 'hidden',
    boxShadow: '0 0.125rem 0.25rem rgba(0,0,0,.075)',
  },
  tableHeader: {
    backgroundColor: '#f8f9fa',
    borderBottom: '2px solid #dee2e6',
  },
  tableHeaderCell: {
    padding: '12px 15px',
    textAlign: 'left',
    fontWeight: 600,
    color: '#495057',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: '#e9ecef',
    },
  },
  sortIcon: {
    marginLeft: '5px',
    fontSize: '0.75rem',
  },
  tableRow: {
    borderBottom: '1px solid #dee2e6',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease-in-out',
    '&:hover': {
      backgroundColor: '#f8f9fa',
    },
  },
  tableCell: {
    padding: '12px 15px',
  },
  badge: {
    display: 'inline-block',
    padding: '0.25em 0.6em',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    verticalAlign: 'baseline',
    borderRadius: '0.25rem',
    backgroundColor: '#007bff',
    color: 'white',
    marginRight: '5px',
  },
  adminBadge: {
    backgroundColor: '#28a745',
  },
  companyAdminBadge: {
    backgroundColor: '#dc3545',
  },
  notCompletedText: {
    color: '#6c757d',
    fontSize: '0.875rem',
    marginLeft: '10px',
  },
  profilePicture: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    objectFit: 'cover',
    backgroundColor: '#f3f3f3',
  },
  profilePictureCell: {
    width: 50,
    padding: '8px 15px',
  },
}));

type SortField = 'name' | 'title' | 'email' | 'email-domain' | 'admin' | 'group' | 'office' | 'status';
type SortDirection = 'asc' | 'desc';

const AllUsersPage = () => {
  const classes = useStyles();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<UserDto | undefined>();
  const [userModalType, setUserModalType] = useState<UserModalType | undefined>();
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const { data: searchResult, isLoading: fetchingUsers } = useUserSearchQuery({ includeAdmins: true, includeUncompleted: true }, { refetchOnMountOrArgChange: true, refetchOnReconnect: true });
  const users = searchResult?.users as UserDto[] | undefined;
  const { data: groupsRes } = useGetGroupsQuery();
  const { data: offices } = useGetOfficesQuery();
  const { data: currentUser } = useGetCurrentUserQuery();

  const groups = groupsRes?.groups;

  const handleOpenUserModal = (user: UserDto) => {
    setSelectedUser(user);
    if (user.companyAdmin) {
      setUserModalType('company-administrator');
    } else if (user.admin || user.administratorPriviliges) {
      setUserModalType('administrator');
    } else {
      setUserModalType('employee');
    }
  };

  const handleCloseUserModal = () => {
    setSelectedUser(undefined);
    setUserModalType(undefined);
  };

  // Update user data in modal if users list changes.
  useEffect(() => {
    setSelectedUser((prevSelectedUser) => {
      if (!prevSelectedUser) return undefined;
      const updatedUser = users?.find((u) => u._id === prevSelectedUser._id);
      // If user is no longer in the list (e.g., deleted), close the modal
      if (!updatedUser) {
        setUserModalType(undefined);
      }
      return updatedUser;
    });
  }, [users]);

  const getGroupName = (user: UserDto): string => {
    const userGroupId = typeof user.group === 'string' ? user.group : user.group?._id;
    const userSecondaryGroupId = typeof user.secondaryGroup === 'string' ? user.secondaryGroup : user.secondaryGroup?._id;

    if (!userGroupId) return 'Not assigned';

    const group = groups?.find((g: GroupDto) => g._id === userGroupId);
    const primaryGroupName = group?.name || 'Unknown';

    if (userSecondaryGroupId) {
      const secondaryGroup = groups?.find((g: GroupDto) => g._id === userSecondaryGroupId);
      if (secondaryGroup) {
        return `${primaryGroupName} (+ ${secondaryGroup.name})`;
      }
    }

    return primaryGroupName;
  };

  const getOfficeName = (user: UserDto): string => {
    const userOfficeId = typeof user.office === 'string' ? user.office : user.office?._id;
    if (!userOfficeId) return 'Not assigned';

    const office = offices?.find((o: any) => o._id === userOfficeId);
    return office?.name || 'Unknown';
  };

  const handleSort = (field: SortField) => {
    // Special handling for email: cycle through email -> email-domain -> email (reversed)
    if (field === 'email' || field === 'email-domain') {
      if (sortField === 'email' && sortDirection === 'asc') {
        // First click: email desc
        setSortDirection('desc');
      } else if (sortField === 'email' && sortDirection === 'desc') {
        // Second click: switch to domain asc
        setSortField('email-domain');
        setSortDirection('asc');
      } else if (sortField === 'email-domain' && sortDirection === 'asc') {
        // Third click: domain desc
        setSortDirection('desc');
      } else if (sortField === 'email-domain' && sortDirection === 'desc') {
        // Fourth click: back to email asc
        setSortField('email');
        setSortDirection('asc');
      } else {
        // Default: start with email asc
        setSortField('email');
        setSortDirection('asc');
      }
    } else {
      // Normal sorting for other fields
      if (sortField === field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
    }
  };

  const filteredUsers = users?.filter((user) =>
    (user.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (user.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    getGroupName(user).toLowerCase().includes(searchQuery.toLowerCase()) ||
    getOfficeName(user).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getAdminSortValue = (user: UserDto): number => {
    if (user.companyAdmin) return 3;
    if (user.administratorPriviliges) return 2;
    if (user.admin) return 1;
    return 0;
  };

  const sortedUsers = filteredUsers?.sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    switch (sortField) {
      case 'name':
        aValue = (a.name || a.email || '').toLowerCase();
        bValue = (b.name || b.email || '').toLowerCase();
        break;
      case 'title':
        aValue = (a.title || '').toLowerCase();
        bValue = (b.title || '').toLowerCase();
        break;
      case 'email':
        aValue = (a.email || '').toLowerCase();
        bValue = (b.email || '').toLowerCase();
        break;
      case 'email-domain':
        // Extract domain from email (everything after @)
        aValue = (a.email || '').toLowerCase().split('@')[1] || '';
        bValue = (b.email || '').toLowerCase().split('@')[1] || '';
        break;
      case 'admin':
        aValue = getAdminSortValue(a);
        bValue = getAdminSortValue(b);
        break;
      case 'group':
        aValue = getGroupName(a).toLowerCase();
        bValue = getGroupName(b).toLowerCase();
        break;
      case 'office':
        aValue = getOfficeName(a).toLowerCase();
        bValue = getOfficeName(b).toLowerCase();
        break;
      case 'status':
        aValue = a.completed ? 'active' : 'pending';
        bValue = b.completed ? 'active' : 'pending';
        break;
      default:
        aValue = '';
        bValue = '';
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div>
      <UserModal
        isOpen={userModalType !== undefined}
        handleClose={handleCloseUserModal}
        user={selectedUser}
        type={userModalType}
      />
      <div className={classNames('topbar', classes.topbar)}>
        <Col
          sm="12"
          md={{
            size: 10,
            offset: 1,
          }}
        >
          <SearchIcon />
          <input
            className={classNames(classes.searchInput, 'search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            type="text"
            placeholder={'Search user, group, or office'}
          />
        </Col>
      </div>
      <Row className={classes.content}>
        <Col
          sm="12"
          md={{
            size: 10,
            offset: 1,
          }}
        >
          <Link className="nav-link back-link" to="/groups" style={{ paddingLeft: 0 + 'px', color: 'grey' }}>
            <svg height="25" width="15" viewBox="0 7 30 20">
              <polyline points="15,0 5,15 15,30" strokeWidth="8" strokeLinecap="round" fill="none" stroke="grey" />
            </svg>
            Back to Groups
          </Link>
          <PageTitle title="All Users" />
          <br />
          <br />
          {fetchingUsers ? (
            <ReactLoading type={'spinningBubbles'} className={classes.loader} />
          ) : (
            <table className={classes.table}>
              <thead className={classes.tableHeader}>
                <tr>
                  <th className={classNames(classes.tableHeaderCell, classes.profilePictureCell)}></th>
                  <th className={classes.tableHeaderCell} onClick={() => handleSort('name')}>
                    Name
                    {sortField === 'name' && (
                      <span className={classes.sortIcon}>{sortDirection === 'asc' ? '▼' : '▲'}</span>
                    )}
                  </th>
                  <th className={classes.tableHeaderCell} onClick={() => handleSort('title')}>
                    Title
                    {sortField === 'title' && (
                      <span className={classes.sortIcon}>{sortDirection === 'asc' ? '▼' : '▲'}</span>
                    )}
                  </th>
                  <th className={classes.tableHeaderCell} onClick={() => handleSort('email')}>
                    Email{sortField === 'email-domain' ? ' (Domain)' : ''}
                    {(sortField === 'email' || sortField === 'email-domain') && (
                      <span className={classes.sortIcon}>{sortDirection === 'asc' ? '▼' : '▲'}</span>
                    )}
                  </th>
                  <th className={classes.tableHeaderCell} onClick={() => handleSort('admin')}>
                    Admin
                    {sortField === 'admin' && (
                      <span className={classes.sortIcon}>{sortDirection === 'asc' ? '▼' : '▲'}</span>
                    )}
                  </th>
                  <th className={classes.tableHeaderCell} onClick={() => handleSort('group')}>
                    Group
                    {sortField === 'group' && (
                      <span className={classes.sortIcon}>{sortDirection === 'asc' ? '▼' : '▲'}</span>
                    )}
                  </th>
                  <th className={classes.tableHeaderCell} onClick={() => handleSort('office')}>
                    Office
                    {sortField === 'office' && (
                      <span className={classes.sortIcon}>{sortDirection === 'asc' ? '▼' : '▲'}</span>
                    )}
                  </th>
                  <th className={classes.tableHeaderCell} onClick={() => handleSort('status')}>
                    Status
                    {sortField === 'status' && (
                      <span className={classes.sortIcon}>{sortDirection === 'asc' ? '▼' : '▲'}</span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers?.map((user) => (
                  <tr
                    key={user._id}
                    className={classes.tableRow}
                    onClick={() => handleOpenUserModal(user)}
                  >
                    <td className={classNames(classes.tableCell, classes.profilePictureCell)}>
                      {user.profilePicture ? (
                        <img
                          src={
                            'https://d378wr66tg5qg7.cloudfront.net/' + user.profilePicture
                          }
                          alt={user.name || ''}
                          className={classes.profilePicture}
                        />
                      ) : (
                        <div className={classes.profilePicture} />
                      )}
                    </td>
                    <td className={classes.tableCell}>{user.name || '-'}</td>
                    <td className={classes.tableCell}>{user.title || '-'}</td>
                    <td className={classes.tableCell}>{user.email}</td>
                    <td className={classes.tableCell}>
                      {user.companyAdmin && (
                        <span className={classNames(classes.badge, classes.companyAdminBadge)}>
                          Company Admin
                        </span>
                      )}
                      {!user.companyAdmin && (
                        <>
                          {user.administratorPriviliges && (
                            <span className={classNames(classes.badge, classes.adminBadge)}>
                              Admin
                            </span>
                          )}
                          {user.admin && (
                            <span className={classNames(classes.badge)}>
                              Observer
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td className={classes.tableCell}>{getGroupName(user)}</td>
                    <td className={classes.tableCell}>{getOfficeName(user)}</td>
                    <td className={classes.tableCell}>
                      {user.completed ? (
                        <span style={{ color: '#28a745' }}>Active</span>
                      ) : (
                        <span className={classes.notCompletedText}>Pending</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default AllUsersPage;
